<template>
  <custom-popup
    v-model="internalDialog"
    :cons="{ title: 'خواندن/ نوشتن کارت NFC', icon: 'card', color: 'green' }"
    hide-confirm
    max-width="650"
  >
    <v-card class="pa-3 glass-card" elevation="0">
      <v-card-title class="font-weight-bold">
        <v-icon left class="mx-2"  :color="isConnected ? 'green' : 'red'">
          {{ isConnected ? 'mdi-wifi' : 'mdi-wifi-off' }}
        </v-icon>
        وضعیت ارتباط NFC
      </v-card-title>

      <v-card-text>
        <v-alert :type="isConnected ? 'success' : 'error'" text outlined>
          {{ statusMessage }}
        </v-alert>
      </v-card-text>

      <custom-tabs v-model="internalTab" :tab-items="tabItems" grow>
        <template v-slot:staticItems>
          <v-tabs-items v-model="internalTab" class="pa-4">
            
            <!-- ================= تب 1: خواندن و جستجو (Read) ================= -->
            <v-tab-item>
              <v-card-text class="text-center pt-8 pb-8">
                <div class="mb-4">
                  <v-icon size="80" :color="isConnected ? 'primary' : 'grey'" :class="{'nfc-pulse': isConnected}">
                    mdi-nfc-search-variant
                  </v-icon>
                </div>
                <h3 class="text-h6 mb-2">آماده اسکن کارت...</h3>
                <p class="text-muted">کارت را روی دستگاه قرار دهید. خواندن و جستجو خودکار انجام می‌شود.</p>
                
                <v-slide-y-transition>
                  <div v-if="uid" class="mt-4">
                    <v-alert type="info" text border="left" dir="ltr" dense>
                      UID: {{ uid }}
                    </v-alert>
                    <v-alert v-if="readUrl" type="success" text border="left" dir="ltr" dense>
                      Link: {{ readUrl }}
                    </v-alert>
                  </div>
                </v-slide-y-transition>
              </v-card-text>
            </v-tab-item>

            <!-- ================= تب 2: نوشتن (Write) ================= -->
            <v-tab-item>
              <v-card-text class="pt-6 pb-6">
                
                <v-alert v-if="batchQueue.length > 1" color="info" text border="right">
                  <div class="d-flex justify-space-between align-center">
                    <div>
                      <div class="font-weight-bold mb-1">حالت رایت گروهی فعال است</div>
                      <strong class="text-primary">کارت {{ currentBatchIndex + 1 }} از {{ batchQueue.length }}</strong>
                      
                      <!-- نمایش شمارش معکوس در حالت خودکار -->
                      <div v-if="countdown > 0 && isAutoNext" class="error--text mt-2 font-weight-bold">
                        لطفاً کارت را بردارید... شروع کارت بعدی در: {{ countdown }} ثانیه
                      </div>

                      <!-- نمایش دکمه "کارت بعدی" در حالت دستی -->
                      <v-slide-y-transition>
                        <div v-if="waitingForNextCard && !isAutoNext" class="mt-3">
                          <v-btn class="btn-get-code rounded-lg" small @click="goToNextCard">
                            <v-icon right class="ml-1">mdi-skip-next</v-icon>
                            تایید و رایت کارت بعدی
                          </v-btn>
                        </div>
                      </v-slide-y-transition>
                    </div>

                    <v-btn class="btn-delete rounded-lg" small depressed @click="stopBatchProcess">
                      <v-icon class="mx-2" right>mdi-stop-circle</v-icon>
                      توقف
                    </v-btn>
                  </div>
                  <v-progress-linear 
                    :value="(currentBatchIndex / batchQueue.length) * 100" 
                    color="info" 
                    class="mt-3"
                    height="8"
                    rounded
                  ></v-progress-linear>
                </v-alert>

                <v-expand-transition>
                  <div v-if="currentBatchIndex === 0 || batchQueue.length > 1">
                      <v-card outlined class="mb-4 pa-3" style="background-color: rgba(255,255,255,0.05);">
                        
                        <div v-if="batchQueue.length <= 1 && currentBatchIndex === 0">
                          <v-checkbox
                            v-model="isRepeatMode"
                            label="رایت یک لینک روی چند کارت (تکراری)"
                            color="success"
                            dense
                            hide-details
                            class="mb-3"
                          ></v-checkbox>
                          
                          <v-text-field
                            v-if="isRepeatMode"
                            v-model.number="repeatCount"
                            type="number"
                            label="تعداد کارت‌ها برای رایت"
                            outlined
                            dense
                            min="2"
                            hide-details
                            class="mb-3"
                          ></v-text-field>

                          <v-checkbox
                            v-model="useDefaultUrl"
                            label="استفاده از لینک پیش‌فرض سیستم"
                            color="success"
                            dense
                            hide-details
                            class="mb-3"
                            @change="toggleDefaultUrl"
                          ></v-checkbox>
                        </div>

                        <!-- حالت سخت‌گیرانه -->
                        <v-checkbox
                          v-model="isStrictWrite"
                          label="رایت (بررسی سلامت مشابه NFC Tools)"
                          color="success"
                          dense
                          hide-details
                          class="mb-3"
                        ></v-checkbox>

                        <v-checkbox
                          v-if="isRepeatMode || batchQueue.length > 1"
                          v-model="isAutoNext"
                          label="رفتن خودکار به کارت بعدی (تایمر دار)"
                          color="success"
                          dense
                          hide-details
                          class="mb-1"
                        ></v-checkbox>

                      </v-card>
                  </div>
                </v-expand-transition>

                <!-- ورودی لینک و انتخابگر پروتکل اصلاح شده -->
                <div class="d-flex flex-row align-end mt-2">
                  <v-text-field
                    v-model="internalUrl"
                    label="لینک آماده رایت (بدون پروتکل)"
                    outlined
                    dense
                    dir="ltr"
                    prepend-inner-icon="mdi-link"
                    clearable
                    hide-details
                    :disabled="batchQueue.length > 1"
                  ></v-text-field>
                                    <v-select            
                    class="ml-2"
                    style="max-width: 120px; border-radius: 8px;"
                    dense
                    :items="['http://','https://']"
                    v-model="internalUrl_p"
                    :menu-props="{offsetY: true}" 
                    outlined
                    hide-details
                  ></v-select>
                  
                </div>
                
                <v-btn
                  block
                  elevation="2"
                  :class="batchQueue.length > 1 ? `btn-get-code rounded-lg mt-4`:`btn-edit rounded-lg mt-4`"
                  :loading="isWriting"
                  :disabled="!internalUrl || !isConnected"
                  @click="startWriteProcessBtn"
                >
                  <v-icon class="mx-2" right>mdi-nfc-tap</v-icon>
                  {{ isWriting ? 'در حال نوشتن...' : (batchQueue.length > 1 ? 'در حال رایت گروهی (صبر کنید)' : 'شروع نوشتن روی کارت') }}
                </v-btn>
              </v-card-text>
            </v-tab-item>

            <!-- ================= تب 3: پاک کردن (Clear) ================= -->
            <v-tab-item>
              <v-card-text class="text-center pt-8 pb-8">
                 <v-icon size="80" color="error" class="mb-4">mdi-eraser</v-icon>
                 <h3 class="text-h6 mb-4">پاک کردن اطلاعات کارت</h3>
                 <p class="text-muted mb-6">کارت را روی دستگاه قرار داده و دکمه زیر را بزنید تا کارت خام شود.</p>
                 
                 <v-btn
                  block
                  elevation="2"
                  class="btn-delete rounded-lg"
                  :disabled="!isConnected"
                  @click="clearCard"
                >
                  <v-icon class="mx-2" right>mdi-eraser-variant</v-icon>
                  پاک کردن کارت
                </v-btn>
              </v-card-text>
            </v-tab-item>

            <!-- ================= تب 4: بررسی سلامت (Health Check) ================= -->
            <v-tab-item>
              <v-card-text class="text-center pt-8 pb-8">
                 <v-icon 
                   size="80" 
                   :color="healthStatus === true ? 'success' : (healthStatus === false ? 'error' : 'primary')" 
                   class="mb-4"
                 >
                   mdi-heart-pulse
                 </v-icon>
                 <h3 class="text-h6 mb-4">بررسی سلامت ساختاری کارت</h3>
                 <p class="text-muted mb-6">کارت را روی ریدر قرار دهید تا از نظر خرابی حافظه (Corrupted) و قفل بودن بررسی شود.</p>
                 
                 <v-btn
                  block
                  elevation="2"
                  class="btn-show rounded-lg mb-4"
                  :disabled="!isConnected"
                  @click="checkCardHealth"
                >
                  <v-icon class="mx-2" right>mdi-stethoscope</v-icon>
                  بررسی کارت   
                </v-btn>

                <v-slide-y-transition>
                  <v-alert v-if="healthStatus !== null" :type="healthStatus ? 'success' : 'error'" text border="left" dense>
                    {{ healthMessage }}
                  </v-alert>
                </v-slide-y-transition>
              </v-card-text>
            </v-tab-item>

            <!-- ================= تب 5: قفل و امنیت (Lock/Unlock) ================= -->
            <v-tab-item>
              <nfc-lock-manager
                v-if="socket"
                :socket="socket"
                :is-connected="isConnected"
                @status-update="statusMessage = $event"
              />
            </v-tab-item>
          <!-- ================= تب 6: بررسی سازگاری NFC Tools ================= -->
            <v-tab-item>
              <v-card-text class="text-center pt-8 pb-8">
                 <v-icon 
                   size="80" 
                   :color="nfctoolsStatus === true ? 'success' : (nfctoolsStatus === false ? 'error' : 'primary')" 
                   class="mb-4"
                 >
                   mdi-cellphone-check
                 </v-icon>
                 <h3 class="text-h6 mb-4">بررسی سازگاری با NFC Tools</h3>
                 <p class="text-muted mb-6">کارت را روی ریدر قرار دهید تا سازگاری آن با نرم‌افزارهای موبایلی (مانند NFC Tools) بررسی شود.</p>
                 
                 <v-btn
                  block
                  elevation="2"
                  class="btn-show rounded-lg mb-4"
                  :disabled="!isConnected"
                  @click="checkNfcTools"
                >
                  <v-icon class="mx-2" right>mdi-check-decagram</v-icon>
                  بررسی سازگاری   
                </v-btn>

                <v-slide-y-transition>
                  <v-alert v-if="nfctoolsStatus !== null" :type="nfctoolsStatus ? 'success' : 'error'" text border="left" dense>
                    {{ nfctoolsMessage }}
                  </v-alert>
                </v-slide-y-transition>
              </v-card-text>
            </v-tab-item>


          </v-tabs-items>
        </template>
      </custom-tabs>
    </v-card>
  </custom-popup>
</template>

<script>
import { io } from "socket.io-client";
import NfcLockManager from './NfcLockManager.vue';

export default {
  name: "NfcManagerRW",
  components: { NfcLockManager },
  props: {
    nfcRW: { type: Boolean, default: false },
    initialUrl: { type: [String, Array], default: () => "" },
    tab: { type: Number, default: 0 },
    
  },
  data() {
    return {
      internalTab:0,
       nfctoolsStatus: null,
      nfctoolsMessage: "",
      internalUrl_p: 'https://',
      isAutoNext: true,       
      waitingForNextCard: false,
      isRepeatMode: false,
      isStrictWrite: false, // رایت سخت‌گیرانه
      repeatCount: 10,
      countdown: 0,
      countdownInterval: null,
      batchTimeout: null,
      tabItems: [
        { text: "خواندن و جستجو", icon: "cellphone-nfc" },
        { text: "نوشتن", icon: "nfc-variant" },
        { text: "پاک کردن", icon: "eraser-variant" },
        { text: "بررسی سلامت", icon: "heart-pulse" },
        { text: "قفل و امنیت", icon: "lock-smart" }, 
        { text: "سازگاری NFC Tools", icon: "cellphone-check" }, 
      ],

      // internalTab: 0,
      socket: null,
      isConnected: false,
      statusMessage: "در حال اتصال به دستگاه...",
      
      uid: "",
      readUrl: "",
      
      internalUrl: "",
      batchQueue: [],
      currentBatchIndex: 0,
      isWriting: false,
      defaultDomain: "qrland.ir", 
      useDefaultUrl: false, 

      healthStatus: null,
      healthMessage: ""
    };
  },
  computed: {
    internalDialog: {
      get() { return this.nfcRW; },
      set(val) { this.$emit("update:nfcRW", val); }
    },
    // internalTab: {
    //   get() { return this.tab; },
    //   set(val) { this.$emit("update:tab", val); }
    // }
  },
  watch: {
    initialUrl: {
      immediate: true,
      handler(newVal) {
        this.initData(newVal);
      }
    },
     tab: {
      immediate: true,
      handler(newVal) {
        if (this.internalTab !== newVal) {
          this.internalTab = newVal;
        }   
       },
      },
 internalTab(newVal) {
      // آپدیت کردن والد
      this.$emit("update:tab", newVal);      
      
      // لاجیک ریست کردن هنگام تغییر تب
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      if (this.batchTimeout) clearTimeout(this.batchTimeout);
      this.countdown = 0;
      this.uid = "";
      this.readUrl = "";
      
      this.healthStatus = null;
      this.healthMessage = "";

      this.nfctoolsStatus = null;
      this.nfctoolsMessage = "";
      
      this.statusMessage = "آماده عملیات...";
    },
    },
    mounted() {
    this.connectToPythonBridge();
  },
  methods: {
     checkNfcTools() {
      this.nfctoolsStatus = null;
      this.nfctoolsMessage = "";
      this.statusMessage = "در حال بررسی سازگاری با NFC Tools...";
      this.socket.emit("nfc-check-nfctools");
    },
    extractProtocol(urlStr) {
      if (!urlStr) return;
      if (urlStr.startsWith('https://')) {
        this.internalUrl_p = 'https://';
        this.internalUrl = urlStr.replace('https://', '');
      } else if (urlStr.startsWith('http://')) {
        this.internalUrl_p = 'http://';
        this.internalUrl = urlStr.replace('http://', '');
      } else {
        this.internalUrl = urlStr;
      }
    },

    goToNextCard() {
      this.waitingForNextCard = false;
      this.$toast.info(' کارت بعدی را قرار دهید...', { timeout: 2000 });
      this.statusMessage = "منتظر کارت بعدی...";
      this.startWriteProcess();
    },
    
    stopBatchProcess() {
      if (this.batchTimeout) clearTimeout(this.batchTimeout);
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      this.countdown = 0;
      this.batchQueue = [];
      this.currentBatchIndex = 0;
      this.isWriting = false;
      this.isRepeatMode = false;
      this.statusMessage = "عملیات گروهی توسط کاربر متوقف شد.";
      this.$toast.warning("رایت گروهی لغو شد.");
    },

    startWriteProcessBtn() {
      if (this.isRepeatMode && this.repeatCount > 1 && this.batchQueue.length === 0) {
        let finalUrl = this.internalUrl;
        if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
          finalUrl = this.internalUrl_p + finalUrl;
        }
        this.batchQueue = Array(Number(this.repeatCount)).fill(finalUrl);
        this.currentBatchIndex = 0;
      }
      this.startWriteProcess();
    },
    
    toggleDefaultUrl(val) {
      if (val) {
        this.internalUrl_p = "https://";
        this.internalUrl = this.defaultDomain;
      } else {
        this.internalUrl = "";
      }
    },
    
    initData(val) {
      if (Array.isArray(val) && val.length > 0) {
        this.batchQueue = [...val];
        this.currentBatchIndex = 0;
        this.extractProtocol(this.batchQueue[0]);
        this.useDefaultUrl = false;
        this.internalTab = 1; 
      } else {
        this.batchQueue = [];
        this.currentBatchIndex = 0;
        this.extractProtocol(val || ""); 
        this.useDefaultUrl = false;
        
        if (this.internalUrl) {
          if (this.internalTab === 0 && (this.internalUrl_p + this.internalUrl) === this.readUrl) {
             // ماندن در تب خواندن
          } else {
             this.internalTab = 1;
          }
        }
      }
    },
    checkCardHealth() {
      this.healthStatus = null;
      this.healthMessage = "";
      this.statusMessage = "در حال بررسی سلامت کارت...";
      this.socket.emit("nfc-check-health");
    },

    connectToPythonBridge() {
      if (this.socket) return;
      
      this.socket = io("http://localhost:3000");

      this.socket.on("connect", () => {
        this.isConnected = true;
        this.statusMessage = "متصل. آماده عملیات...";
      });

      this.socket.on("disconnect", () => {
        this.isConnected = false;
        this.statusMessage = "ارتباط با ماژول قطع شد!";
      });

      this.socket.on("nfc-uid", (data) => {
        this.uid = data.uid;
        this.statusMessage = "کارت شناسایی شد.";
        this.$emit("scanned", data.uid);

        if (this.internalTab === 0) {
          this.socket.emit("nfc-read");
          this.statusMessage = "در حال استخراج لینک کارت...";
        } 
        else if( this.internalTab === 4){
          this.socket.emit("nfc-heard_check"); 
        }
        else if (this.internalTab === 3) { 
          this.checkCardHealth();
        }
        else if (this.internalTab === 5) { 
          this.checkNfcTools();
        }
      });

      this.socket.on("nfc-read-status", (res) => {
        if (res.status === 'success') {
          this.readUrl = res.url || "";
          if (this.internalTab === 0) {
            if (this.readUrl) {
              this.$toast.success(res.message || "لینک خوانده شد.");
            } else {
              this.$toast.info(res.message || "کارت خام است و لینکی ندارد.");
            }
            this.statusMessage = res.message || "خواندن موفقیت‌آمیز بود.";
            this.$emit("update-search", res.url);
          }
        } else {
          this.readUrl = ""; 
          this.statusMessage = res.message || "خطا در خواندن کارت";
          if (this.internalTab === 0) {
            this.$toast.error(res.message || "خطا در خواندن");
          }
        }
        
      });
      this.socket.on("nfctools-status", (res) => {
        this.nfctoolsStatus = res.is_compatible;
        this.nfctoolsMessage = res.message;
        
        if (res.is_compatible) {
          if (this.internalTab === 5) this.$toast.success("کارت با گوشی و NFC Tools سازگار است");
          this.statusMessage = "سازگاری کارت تأیید شد.";
        } else {
          if (this.internalTab === 5) this.$toast.error("کارت با NFC Tools سازگار نیست!");
          this.statusMessage = res.message;
        }
      });

      this.socket.on("nfc-health-status", (res) => {
        this.healthStatus = res.is_healthy;
        this.healthMessage = res.message;
        
        if (res.is_healthy) {
          if (this.internalTab === 3) this.$toast.success("کارت از لحاظ ساختاری سالم است");
          this.statusMessage = "سلامت کارت تأیید شد.";
        } else {
          if (this.internalTab === 3) this.$toast.error("کارت دارای ایراد ساختاری است");
          this.statusMessage = res.message;
        }
      });

      this.socket.on("nfc-write-status", (res) => {
        if (this.internalTab === 1 || this.internalTab === 2) {
          if (res.status === 'success') {
            this.isWriting = false;
            this.statusMessage = res.message || "عملیات با موفقیت انجام شد.";
            
           if (this.internalTab === 1) {
            if (this.batchQueue.length > 1) {
              this.currentBatchIndex++; 
              
              if (this.currentBatchIndex < this.batchQueue.length) {
                this.extractProtocol(this.batchQueue[this.currentBatchIndex]);
                
                this.$toast.success(' رایت شد! لطفا کارت را بردارید...', { timeout: 2000 });
                this.statusMessage = "کارت را بردارید...";
                
                if (this.isAutoNext) {
                  this.countdown = 4;
                  if (this.countdownInterval) clearInterval(this.countdownInterval);
                  
                  this.countdownInterval = setInterval(() => {
                    this.countdown--;
                    if (this.countdown <= 0) {
                      clearInterval(this.countdownInterval);
                      if (this.internalTab === 1 && this.batchQueue.length > 0) {
                        this.$toast.info(' کارت بعدی را قرار دهید...', { timeout: 2000 });
                        this.statusMessage = "منتظر کارت بعدی...";
                        this.startWriteProcess(); 
                      }
                    }
                  }, 1000);
                } else {
                  this.waitingForNextCard = true;
                  this.statusMessage = "منتظر تایید برای رایت کارت بعدی...";
                }

              } else {
                if (this.countdownInterval) clearInterval(this.countdownInterval);
                this.countdown = 0;
                this.waitingForNextCard = false;
                this.$toast.success(' عملیات گروهی با موفقیت تمام شد!');
                this.batchQueue = [];
                this.currentBatchIndex = 0;
                this.statusMessage = "عملیات کامل شد.";
              }
            } else {
              this.$toast.success(res.message || "عملیات نوشتن انجام شد.");
            }
          } else if (this.internalTab === 2) {
              this.$toast.success(res.message || "کارت با موفقیت پاک (خام) شد.");
            }
          } 
          else {
            this.isWriting = false;
            this.statusMessage = res.message || "خطا در انجام عملیات!";
            this.$toast.error(res.message || "خطا رخ داد");
            
            if (this.internalTab === 1 && this.batchQueue.length > 1) {
              this.statusMessage = "خطا! کارت دیگری قرار دهید...";
              this.batchTimeout = setTimeout(() => {
                if (this.internalTab !== 1 || this.batchQueue.length === 0) return;
                this.$toast.warning('آماده رایت مجدد...', { timeout: 3000 });
                this.startWriteProcess(); 
              }, 2000);
            }
          }
        }
      });
    },

    startWriteProcess() {
      let finalUrl = this.internalUrl;
      
      if (!finalUrl) {
        this.$toast.error("لطفاً یک لینک وارد کنید");
        return;
      }

      // ترکیب پروتکل انتخاب شده با لینک
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        finalUrl = this.internalUrl_p + finalUrl;
      }

      this.isWriting = true;
      this.statusMessage = "در حال نوشتن: " + finalUrl;
      
      if (this.isStrictWrite) {
        // ارسال فرمان رایت سخت‌گیرانه به سوکت
        this.socket.emit("nfc-strict-write", { url: finalUrl });
      } else {
        // رایت معمولی
        this.socket.emit("nfc-write", { url: finalUrl });
      }
    },

    clearCard() {
      this.socket.emit("nfc-clear");
      this.statusMessage = "در حال پاک کردن کارت...";
    }
  },
  beforeDestroy() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
    if (this.batchTimeout) clearTimeout(this.batchTimeout);
    if (this.socket) {
      this.socket.disconnect();
    }
  }
};
</script>

<style scoped>
.nfc-pulse {
  animation: pulse-animation 5s ease-in-out infinite;
}

@keyframes pulse-animation {
  0% { 
    transform: scale(1);
    opacity: 1;
  }
  50% { 
    transform: scale(1.05); 
    opacity: 0.6; 
  }
  100% { 
    transform: scale(1);
    opacity: 1;
  }
}
</style>
